import React, { Fragment, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { GetRemarks, AddRemark, DeleteRemark, AddRemarkDoc } from '../../REST/utilities'
import { useForm, Controller } from "react-hook-form";
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import NotesIcon from '@mui/icons-material/NoteAdd';
import DocumentView from './documents';
import NoteIcon from '@mui/icons-material/Note';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Access from '../../Utils/authorize';
import { Typography } from '@mui/material';
import { dateFormat } from '../../helpers/dates'
import ResponsiveDialog from '../../themes/components/ResponsiveDialog';
import ToolDialogShell from '../../themes/components/ToolDialogShell';

function Remarks({ docIds = [], onUpload, module }) {
    const [data, setData] = useState([])
    const [mode, setMode] = useState(0)
    const [refresh, setRefresh] = useState(false)

    const toggleRefresh = () => setRefresh(r => !r)

    useEffect(() => {
        (async () => {
            const result = await GetRemarks(docIds)
            setData(result.data)
            setMode(0)
        })()
    }, [refresh, docIds])

    useEffect(() => toggleRefresh, [])

    const handleMode = setMode

    const handleData = (data) => {
        (async () => {
            const result = await AddRemark({ ...data, createdBy: Access.getFullname() })
            onUpload(result.data)
            setMode(0)
            toggleRefresh()
        })()
    }

    const addDocument = (id, doc) => AddRemarkDoc(id, doc).then(toggleRefresh)

    const handleDelete = (id) => DeleteRemark(id).then(toggleRefresh)

    return <Fragment>
        <FormDialog open={mode !== 0} handleClose={() => handleMode(0)} handleSave={handleData} />
        <Stack spacing={3}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
            >
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Remarks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {data.length > 0 ? `${data.length} remark${data.length === 1 ? '' : 's'} added` : 'No remarks added yet.'}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleMode(-1)}>
                    Add Remark
                </Button>
            </Stack>

            <Stack spacing={2}>
                {data.map((value) => (
                    <Paper key={value._id} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {value.subject}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Added {dateFormat(value.createdAt)} by {value.createdBy || 'Unknown'}
                                </Typography>
                            </Box>

                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {value.message}
                            </Typography>

                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <DocumentView
                                    module={module}
                                    icon
                                    docIds={value.documents}
                                    onUpload={r => addDocument(value._id, r._id)}
                                />
                                {Access.isAdmin(module) && (
                                    <IconButton color="error" onClick={() => handleDelete(value._id)} edge="end">
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </Stack>
    </Fragment>
}


function FormDialog({ open = false, handleClose, handleSave }) {
    const schema = yup.object({
        subject: yup.string().trim().required(),
        message: yup.string().trim().required()
    }).required();

    const { control, handleSubmit, formState: { errors }, clearErrors, reset } = useForm({
        defaultValues: {
            subject: '',
            message: ''
        },
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        clearErrors()
        reset()
    }, [open, clearErrors, reset])

    const onSubmit = handleSave

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'


    return <ResponsiveDialog open={open} onClose={handleClose}>
        <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
                <Typography variant="h6">Add Remark</Typography>
                <Controller name="subject" control={control} render={({ field }) => <TextField error={isError(errors.subject)} helperText={errors.subject && errors.subject.message} fullWidth label='Subject' {...field} />} />
                <Controller name="message" control={control} render={({ field }) => <TextField error={isError(errors.message)} helperText={errors.message && errors.message.message} fullWidth label='Message' multiline minRows={4} {...field} />} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit(onSubmit)}>Save</Button>
                </Stack>
            </Stack>
        </Box>
    </ResponsiveDialog>
}


export default function RemarksView({
    docIds = [],
    onUpload,
    icon = false,
    fullWidth = false,
    module,
    open: openProp,
    onClose,
    showTrigger = true,
    buttonProps = {},
}) {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const open = openProp ?? internalOpen;

    const handleClickOpen = () => {
        if (openProp === undefined) setInternalOpen(true);
    };

    const handleClose = () => {
        if (openProp === undefined) setInternalOpen(false);
        onClose?.();
    };

    return (
        <Fragment>
            {showTrigger && !icon && <Button variant="contained" startIcon={<NoteIcon />} onClick={handleClickOpen} {...buttonProps}>
                Remarks
            </Button>}
            {showTrigger && icon && <IconButton onClick={handleClickOpen} edge="end">
                <NotesIcon />
            </IconButton>}
            <ToolDialogShell open={open} onClose={handleClose} title="Remarks">
                    <Remarks module={module} onUpload={onUpload} docIds={docIds} />
            </ToolDialogShell>
        </Fragment>
    );
}
